// Email Integration Service
// Supports IMAP for Yahoo, Gmail, and custom domain emails
// Note: Actual IMAP polling requires server-side implementation (Edge Functions)

const EMAIL_CONFIG_KEY = 'hmgc_email_config';

export const EMAIL_PROVIDERS = {
  YAHOO: {
    name: 'Yahoo Mail',
    imapHost: 'imap.mail.yahoo.com',
    imapPort: 993,
    smtpHost: 'smtp.mail.yahoo.com',
    smtpPort: 465,
    requiresAppPassword: true,
    appPasswordUrl: 'https://login.yahoo.com/account/security/app-passwords'
  },
  GMAIL: {
    name: 'Gmail',
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 465,
    requiresAppPassword: true,
    appPasswordUrl: 'https://myaccount.google.com/apppasswords'
  },
  OUTLOOK: {
    name: 'Outlook / Microsoft 365',
    imapHost: 'outlook.office365.com',
    imapPort: 993,
    smtpHost: 'smtp.office365.com',
    smtpPort: 587,
    requiresAppPassword: false,
    note: 'Use your regular password or enable OAuth'
  },
  CUSTOM: {
    name: 'Custom IMAP',
    imapHost: '',
    imapPort: 993,
    smtpHost: '',
    smtpPort: 465,
    requiresAppPassword: false,
    note: 'Enter your mail server details'
  }
};

export const getEmailConfig = () => {
  try {
    const config = localStorage.getItem(EMAIL_CONFIG_KEY);
    return config ? JSON.parse(config) : null;
  } catch (e) {
    console.error('Error reading email config:', e);
    return null;
  }
};

export const saveEmailConfig = (config) => {
  try {
    localStorage.setItem(EMAIL_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (e) {
    console.error('Error saving email config:', e);
    return false;
  }
};

export const isEmailConfigured = () => {
  const config = getEmailConfig();
  return config && config.email && config.password && config.provider;
};

export const getProviderSettings = (provider) => {
  return EMAIL_PROVIDERS[provider] || EMAIL_PROVIDERS.CUSTOM;
};

// Store email messages in localStorage (would be Supabase in production)
const EMAIL_MESSAGES_KEY = 'hmgc_email_messages';

export const getEmailMessages = () => {
  try {
    const messages = localStorage.getItem(EMAIL_MESSAGES_KEY);
    return messages ? JSON.parse(messages) : [];
  } catch (e) {
    return [];
  }
};

export const saveEmailMessages = (messages) => {
  try {
    localStorage.setItem(EMAIL_MESSAGES_KEY, JSON.stringify(messages));
    return true;
  } catch (e) {
    return false;
  }
};

export const addEmailMessage = (message) => {
  const messages = getEmailMessages();
  const newMessage = {
    id: `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    processed: false,
    source: 'email',
    ...message
  };
  messages.unshift(newMessage);
  if (messages.length > 100) {
    messages.splice(100);
  }
  saveEmailMessages(messages);
  return newMessage;
};

export const markEmailProcessed = (messageId, result) => {
  const messages = getEmailMessages();
  const index = messages.findIndex(m => m.id === messageId);
  if (index !== -1) {
    messages[index].processed = true;
    messages[index].processedAt = new Date().toISOString();
    messages[index].result = result;
    saveEmailMessages(messages);
  }
};

export const getUnprocessedEmails = () => {
  return getEmailMessages().filter(m => !m.processed);
};

// Simulate adding an email (for testing without actual IMAP)
export const simulateIncomingEmail = (from, subject, body, customerName = null) => {
  const message = addEmailMessage({
    from,
    subject,
    body,
    customerName,
    simulated: true
  });
  console.log('Email received (simulated):', message);
  return message;
};

// Get last sync timestamp
export const getLastEmailSync = () => {
  try {
    return localStorage.getItem('hmgc_email_last_sync') || null;
  } catch (e) {
    return null;
  }
};

export const setLastEmailSync = (timestamp) => {
  try {
    localStorage.setItem('hmgc_email_last_sync', timestamp || new Date().toISOString());
    return true;
  } catch (e) {
    return false;
  }
};

export default {
  EMAIL_PROVIDERS,
  getEmailConfig,
  saveEmailConfig,
  isEmailConfigured,
  getProviderSettings,
  getEmailMessages,
  addEmailMessage,
  markEmailProcessed,
  getUnprocessedEmails,
  simulateIncomingEmail,
  getLastEmailSync,
  setLastEmailSync
};
